import { useState, useRef } from "react";
import PropTypes from "prop-types";

import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  Icon,
  Chip,
  Divider,
} from "@mui/material";

// ─── Contenido de la documentación ────────────────────────────────────────────
const SECTIONS = [
  {
    id: "expedientes",
    icon: "folder_open",
    title: "Expedientes",
    color: "#1976d2",
    articles: [
      {
        title: "Cómo crear un expediente",
        steps: [
          'Hacé clic en "Nuevo expediente" en el menú lateral.',
          "Completá los datos obligatorios: Número, Año y Carátula.",
          "Opcionalmente, ingresá la Jurisdicción, la Dependencia (juzgado/secretaría) y el Cuaderno.",
          "Si ya tenés las fechas disponibles, registrá la Fecha de Sorteo, Notificación y/o Aceptación del cargo.",
          'Podés pegar el link directo al expediente en el sistema del PJN en el campo "Link expediente".',
          'Hacé clic en "Guardar". El expediente se crea con estado "Nuevo" y queda disponible en tu listado.',
        ],
      },
      {
        title: "Cómo buscar y filtrar expedientes",
        steps: [
          'Ingresá a "Expedientes" desde el menú lateral.',
          "En la barra de búsqueda podés escribir parte del número, año, carátula o jurisdicción.",
          "Usá el filtro de estados para ver solo las causas en un estado determinado (Nuevo, En proceso, Informe enviado, Cobrado, etc.).",
          "Los resultados se actualizan en tiempo real mientras escribís.",
        ],
      },
      {
        title: "Cómo cambiar el estado de un expediente",
        steps: [
          "Ingresá al detalle del expediente que querés actualizar.",
          'Buscá el selector de "Estado" en la sección de datos del expediente.',
          "Seleccioná el nuevo estado que corresponda al avance de la causa.",
          "El cambio se guarda automáticamente.",
          "El dashboard refleja el nuevo estado en las estadísticas de tu cartera.",
        ],
      },
      {
        title: "Cómo agregar una nota a un expediente",
        steps: [
          "Ingresá al detalle del expediente.",
          'Dirigite a la pestaña "Notas".',
          'Completá el campo "Título" (breve identificador de la nota) y el campo "Descripción" con el contenido.',
          'Hacé clic en "Agregar nota".',
          "La nota queda guardada con fecha y hora de creación y aparece en el listado de notas de ese expediente.",
        ],
      },
      {
        title: "Cómo registrar un turno o fecha",
        steps: [
          "Ingresá al detalle del expediente.",
          'Dirigite a la pestaña "Fechas".',
          "Ingresá la fecha y hora del turno.",
          "Completá la Entidad (nombre del juzgado, perito contrario, etc.) y el Contacto si corresponde.",
          "Si el turno es virtual, podés pegar el link de acceso (Zoom, Meet, etc.).",
          "Agregá una descripción opcional para recordar de qué se trata.",
          'Guardá el turno. Aparecerá también en el Dashboard dentro de "Próximas fechas".',
        ],
      },
      {
        title: "Cómo agregar un documento a un expediente",
        steps: [
          "Ingresá al detalle del expediente.",
          'Dirigite a la pestaña "Documentos".',
          'Hacé clic en "Nuevo documento" e ingresá un nombre descriptivo.',
          "Se abre el editor de texto donde podés redactar el contenido del documento.",
          "Podés dar formato al texto usando la barra de herramientas del editor.",
          'Cuando termines, hacé clic en "Guardar". El documento queda asociado al expediente.',
          "Para editar un documento existente, hacé clic en su nombre en el listado.",
        ],
      },
      {
        title: "Cómo registrar tiempo dedicado",
        steps: [
          "Ingresá al detalle del expediente.",
          'Dirigite a la pestaña "Tiempo".',
          "Ingresá la cantidad de tiempo dedicado (en horas o la unidad que uses).",
          "Agregá una descripción de la tarea realizada (ej: «Análisis de documentación contable»).",
          'Hacé clic en "Registrar". Queda un historial de tiempo por expediente.',
          "Este registro te permite justificar el tiempo dedicado al momento de regular honorarios.",
        ],
      },
      {
        title: "Cómo registrar el honorario de un expediente",
        steps: [
          "Ingresá al detalle del expediente y hacé clic en editar.",
          'Buscá el campo "Honorario" e ingresá el monto regulado.',
          "Guardá los cambios.",
          "El Dashboard mostrará este monto en el resumen de honorarios del período.",
        ],
      },
    ],
  },
  {
    id: "plantillas",
    icon: "article",
    title: "Plantillas",
    color: "#388e3c",
    articles: [
      {
        title: "Cómo crear una plantilla",
        steps: [
          'Ingresá a "Plantillas" desde el menú lateral.',
          'Hacé clic en el botón "Nueva plantilla".',
          "Se crea automáticamente una plantilla con el nombre «Nueva plantilla».",
          "Hacé clic en el nombre para renombrarla con algo descriptivo (ej: «Pericia contable - Fuero Civil»).",
          "En el editor de texto de la derecha, redactá el contenido base de la plantilla.",
          'Hacé clic en "Guardar" para conservar los cambios.',
        ],
      },
      {
        title: "Cómo editar una plantilla existente",
        steps: [
          'Ingresá a "Plantillas" desde el menú lateral.',
          "En el listado de la izquierda, hacé clic en la plantilla que querés editar.",
          "El contenido se carga en el editor de la derecha.",
          "Realizá los cambios necesarios en el texto.",
          'Hacé clic en "Guardar". Si intentás salir con cambios sin guardar, el sistema te pregunta si querés continuar.',
        ],
      },
      {
        title: "Cómo usar una plantilla en un documento de expediente",
        steps: [
          "Ingresá al detalle del expediente donde querés usar la plantilla.",
          'En la pestaña "Documentos", creá un nuevo documento o abrí uno existente.',
          "Dentro del editor de documento, buscá la opción para insertar plantilla.",
          "Seleccioná la plantilla del listado. El contenido se inserta en el editor.",
          "Personalizá el texto con los datos específicos de esa causa.",
          "Guardá el documento.",
        ],
      },
      {
        title: "Cómo usar las variables de plantilla",
        steps: [
          'En el panel de "Plantillas", encontrarás el listado de variables disponibles.',
          "Las variables son marcadores que se pueden insertar en el texto de la plantilla.",
          "Usá las variables para los campos que cambian en cada expediente (carátula, número, fecha, etc.).",
          "Cuando la plantilla se use en un documento, reemplazá manualmente cada variable por el dato correspondiente.",
        ],
      },
    ],
  },
  {
    id: "tareas",
    icon: "checklist",
    title: "Tareas",
    color: "#f57c00",
    articles: [
      {
        title: "Cómo crear una tarea",
        steps: [
          'Ingresá a "Tareas" desde el menú lateral.',
          'Completá el campo "Título" con una descripción breve de la tarea.',
          "Opcionalmente, agregá una descripción más detallada.",
          "Si la tarea está vinculada a un expediente, seleccioná el expediente del listado.",
          "Podés asignar una fecha límite para recordar cuándo debe completarse.",
          'Hacé clic en "Agregar tarea". Aparecerá en el listado de tareas pendientes.',
        ],
      },
      {
        title: "Cómo vincular una tarea a un expediente",
        steps: [
          "Al crear o editar una tarea, buscá el selector de expediente.",
          "Seleccioná el expediente correspondiente de la lista.",
          "La tarea queda asociada a esa causa y se muestra con el nombre de la carátula como referencia.",
          "Esto te permite tener contexto de qué causa requiere cada acción pendiente.",
        ],
      },
      {
        title: "Cómo marcar una tarea como completada",
        steps: [
          'En el listado de tareas pendientes, buscá la tarea que finalizaste.',
          'Hacé clic en el botón o ícono para marcarla como "Completada".',
          "La tarea desaparece del listado de pendientes y pasa al historial de tareas realizadas.",
          "Podés ver las tareas completadas cambiando la vista del listado.",
        ],
      },
    ],
  },
  {
    id: "dashboard",
    icon: "dashboard",
    title: "Dashboard",
    color: "#7b1fa2",
    articles: [
      {
        title: "Qué muestra el Dashboard",
        steps: [
          "El Dashboard es la pantalla principal al ingresar al sistema y muestra un resumen de tu práctica.",
          "Expedientes por estado: cuántas causas tenés en cada etapa del proceso.",
          "Estadísticas del período: cantidad de expedientes ingresados y cerrados en los últimos 30 días, con comparativa respecto al mes anterior.",
          "Honorarios del período: total de honorarios registrados recientemente.",
          "Próximas fechas: los próximos 5 turnos o vencimientos registrados en orden cronológico.",
          "Causas inactivas: expedientes activos que llevan más días sin actividad registrada.",
          "Evolución mensual: gráfico con la cantidad de expedientes ingresados por mes en los últimos 12 meses.",
        ],
      },
      {
        title: "Cómo interpretar las causas inactivas",
        steps: [
          'El widget de "Causas inactivas" muestra los expedientes que llevan más tiempo sin ninguna actualización.',
          "Una causa se considera activa si tuvo modificaciones, notas o documentos agregados recientemente.",
          "Usalo como recordatorio para revisar si hay alguna causa que necesita atención o actualización de estado.",
          "Las causas en estado «Cobrado», «Cargo destituido», «Pericia desistida» o «Informe enviado» no aparecen en este listado.",
        ],
      },
    ],
  },
  {
    id: "calendario",
    icon: "calendar_month",
    title: "Calendario",
    color: "#c62828",
    articles: [
      {
        title: "Cómo ver tus fechas en el calendario",
        steps: [
          'Ingresá a "Calendario" desde el menú lateral.',
          "El calendario muestra todas las fechas y turnos que registraste en tus expedientes.",
          "Podés navegar entre meses usando las flechas de la parte superior.",
          "Hacé clic en un evento para ver el detalle: expediente asociado, entidad, contacto y descripción.",
        ],
      },
    ],
  },
];

// ─── Componente de tarjeta de categoría ───────────────────────────────────────
function CategoryCard({ section, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2,
        cursor: "pointer",
        border: "1px solid",
        borderColor: "transparent",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: section.color,
          boxShadow: `0 4px 20px ${section.color}22`,
          transform: "translateY(-2px)",
        },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1.5}>
        <MDBox
          sx={{
            width: 42,
            height: 42,
            borderRadius: "10px",
            background: `${section.color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: section.color, fontSize: "1.4rem !important" }}>
            {section.icon}
          </Icon>
        </MDBox>
        <MDBox>
          <MDTypography variant="button" fontWeight="bold" display="block">
            {section.title}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {section.articles.length} ítem{section.articles.length !== 1 ? "s" : ""}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

const sectionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
});

CategoryCard.propTypes = {
  section: sectionShape.isRequired,
  onClick: PropTypes.func.isRequired,
};

// ─── Componente de sección con accordions ─────────────────────────────────────
function DocSection({ section }) {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Card sx={{ mb: 4, p: 3, borderLeft: `4px solid ${section.color}` }}>
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MDBox
          sx={{
            width: 36,
            height: 36,
            borderRadius: "9px",
            background: `${section.color}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ color: section.color, fontSize: "1.2rem !important" }}>
            {section.icon}
          </Icon>
        </MDBox>
        <MDTypography variant="h5" fontWeight="bold">
          {section.title}
        </MDTypography>
      </MDBox>

      {section.articles.map((article, idx) => (
        <Accordion
          key={idx}
          expanded={expanded === idx}
          onChange={handleChange(idx)}
          disableGutters
          sx={{
            mb: 1,
            background: "transparent",
            border: "1px solid",
            borderColor: expanded === idx ? section.color : "divider",
            borderRadius: "10px !important",
            boxShadow: "none",
            "&:before": { display: "none" },
            transition: "border-color 0.2s",
          }}
        >
          <AccordionSummary
            expandIcon={
              <Icon sx={{ color: expanded === idx ? section.color : "inherit" }}>
                expand_more
              </Icon>
            }
            sx={{
              borderRadius: "10px",
              background: "transparent",
              px: 2.5,
              py: 0.5,
              "& .MuiAccordionSummary-content": { my: 1.5 },
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <Chip
                label={idx + 1}
                size="small"
                sx={{
                  background: expanded === idx ? section.color : "rgba(128,128,128,0.18)",
                  color: expanded === idx ? "#fff" : "text.primary",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  height: 22,
                  width: 22,
                  "& .MuiChip-label": { px: 0 },
                  transition: "all 0.2s",
                }}
              />
              <MDTypography variant="button" fontWeight="medium" color="dark">
                {article.title}
              </MDTypography>
            </MDBox>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 3, pb: 2.5, pt: 0, background: "transparent" }}>
            <Divider sx={{ mb: 2, mt: 0 }} />
            <MDBox component="ol" sx={{ pl: 0, m: 0, listStyle: "none" }}>
              {article.steps.map((step, stepIdx) => (
                <MDBox
                  key={stepIdx}
                  component="li"
                  display="flex"
                  alignItems="flex-start"
                  gap={1.5}
                  mb={stepIdx < article.steps.length - 1 ? 1.5 : 0}
                >
                  <MDBox
                    sx={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: "6px",
                      background: `${section.color}15`,
                      border: `1px solid ${section.color}30`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: "1px",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: section.color, lineHeight: 1 }}
                    >
                      {stepIdx + 1}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="body2" color="dark" sx={{ lineHeight: 1.6 }}>
                    {step}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
          </AccordionDetails>
        </Accordion>
      ))}
    </Card>
  );
}

DocSection.propTypes = {
  section: sectionShape.isRequired,
};

// ─── Página principal ─────────────────────────────────────────────────────────
const Help = () => {
  const sectionRefs = useRef({});

  const scrollToSection = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar title="Centro de ayuda" />

      {/* Encabezado */}
      <MDBox mb={4}>
        <MDTypography variant="h4" fontWeight="bold" gutterBottom>
          ¿En qué podemos ayudarte?
        </MDTypography>
        <MDTypography variant="body2" color="text">
          Guías paso a paso para sacarle el máximo provecho al sistema.
        </MDTypography>
      </MDBox>

      {/* Tarjetas de categorías */}
      <Grid container spacing={2} mb={5}>
        {SECTIONS.map((section) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={section.id}>
            <CategoryCard section={section} onClick={() => scrollToSection(section.id)} />
          </Grid>
        ))}
      </Grid>

      {/* Secciones de documentación */}
      {SECTIONS.map((section) => (
        <MDBox
          key={section.id}
          ref={(el) => (sectionRefs.current[section.id] = el)}
          sx={{ scrollMarginTop: "80px" }}
        >
          <DocSection section={section} />
        </MDBox>
      ))}
    </DashboardLayout>
  );
};

export default Help;
